import { Box, Edges, Line } from '@react-three/drei';
import React, { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Side } from '../../@types/Side';
import StandardWallModel from '../../models/standard/StandardWallModel';
import { distanceToWallNormal } from '../../utils/vectorUtils';
import { snapRadiatorToWall } from './Radiator';

export interface TempRadiatorProps {
  wall: StandardWallModel;
  pointer: THREE.Vector3;
  width: number;
  thickness: number;
  nicheDepth?: number;
}

const TempRadiator = ({
  wall,
  pointer,
  width,
  thickness,
  nicheDepth = 0,
}: TempRadiatorProps) => {

  const side: Side = useMemo(() => {
    const distToInner = distanceToWallNormal(pointer, wall.outerStartPoint2, wall.outerEndPoint2);
    const distToOuter = distanceToWallNormal(pointer, wall.outerStartPoint, wall.outerEndPoint);
    
    return distToInner <= distToOuter ? 'A' : 'B';
  }, [pointer, wall]);

  const snappedPos = useMemo(() => {
    return snapRadiatorToWall(wall, pointer, width, thickness, nicheDepth, side);
  }, [pointer, wall, width, thickness, nicheDepth, side]);

  const angle = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(wall.endPoint, wall.startPoint);
    return Math.atan2(dir.y, dir.x);
  }, [wall.startPoint, wall.endPoint]);

  const verticalLines = useMemo(() => {
    const desiredSpacing = 0.3;
    const numLines = Math.max(Math.floor(width / desiredSpacing) - 1, 0);
    if (numLines === 0) return [];
    const spacing = width / (numLines + 1);
    return Array.from({ length: numLines }, (_, i) => {
      const x = -width / 2 + spacing * (i + 1);
      return new THREE.Vector3(x, 0, 0);
    });
  }, [width]);

  return (
    <group position={snappedPos} rotation={[0, 0, angle]}>
      <Box args={[width, thickness, 1]}>
        <meshBasicMaterial
          transparent
          opacity={0.35}
          color="#66caff"
        />
        <Edges color="black" />
      </Box>

      {verticalLines.map((pos, i) => (
        <Line
          key={`temp-line-${i}`}
          points={[
            new THREE.Vector3(pos.x, -thickness / 2, 0),
            new THREE.Vector3(pos.x, thickness / 2, 0)
          ]}
          color="#333333"
          linewidth={0.5}
          depthTest={false}
        />
      ))}
    </group>
  );
};

export default memo(TempRadiator);